import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuthContext } from './features/auth/context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/layout/Layout';
import Welcome from './features/auth/components/Welcome';
import EmailVerification from './features/auth/components/EmailVerification';
import InvitationVerification from './features/auth/components/InvitationVerification';
import InvitationRequest from './features/auth/components/InvitationRequest';
import ForgotPassword from './features/auth/components/ForgotPassword';
import ResetPassword from './features/auth/components/ResetPassword';
import ProtectedRoute from './features/auth/components/ProtectedRoute';
import Dashboard from './features/dashboard/components/Dashboard';
import CashFlowDashboard from './features/cashflow/components/CashFlowDashboard';
import Wallet from './features/wallet/components/Wallet';
import Transactions from './features/transactions/components/Transactions';
import RecurringTransactions from './features/transactions/components/RecurringTransactions';
import CategoryManagement from './features/transactions/components/CategoryManagement';
import ImportTransactions from './features/transactions/components/ImportTransactions';
import { Toaster } from 'react-hot-toast';
import Profile from './features/auth/components/Profile';
import PartnerSettings from './features/auth/components/PartnerSettings';
import { BudgetList, BudgetDetail, BudgetForm, BudgetActual, BudgetCompare } from './features/budgeting';
import TemplateForm from './features/budgeting/components/templates/TemplateForm';
import BudgetTemplatesPage from './features/budgeting/components/templates/BudgetTemplatesPage';
import About from './features/auth/components/About';
import Mission from './features/auth/components/Mission';
import Docs from './features/auth/components/Docs';
import Forum from './features/auth/components/Forum';
import PrivacyPolicy from './features/static/PrivacyPolicy';
import DataRetentionPolicy from './features/static/DataRetentionPolicy';
import React, { lazy, useEffect } from 'react';
import Register from './features/auth/components/Register';
import ImpersonationBanner from './components/shared/ImpersonationBanner';
import WallyChatModal from './components/shared/WallyChatModal';
// Removed unused imports to prevent conflicts
// import { OnboardingModal } from './features/onboarding'; // DISABLED - Using new onboarding system
import OnboardingFlow from './onboarding/components/OnboardingFlow';
import FeedbackButton from './components/feedback/FeedbackButton';
import FeedbackModal from './components/feedback/FeedbackModal';
import JourneysPage from './features/journeys/components/JourneysPage';
import JourneyDetail from './features/journeys/components/JourneyDetail';

// Lazy load admin portal
const AdminLogin = lazy(() => import('./admin-folder/AdminLogin'));
const AdminPortal = lazy(() => import('./admin-folder'));

function AdminProtectedRoute({ children }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;
  if (!token) {
    console.log('📛 Admin access denied: No admin token found');
    window.location.href = '/admin/login';
    return null;
  }
  console.log('✅ Admin access granted');
  return children;
}

// Admin-only route for regular users (checks user.isAdmin, not adminToken)
function AdminOnlyRoute({ children }) {
  const { user } = useAuthContext();
  if (!user?.isAdmin) {
    console.log('📛 Journeys access denied: User is not an admin');
    return <Navigate to="/dashboard" replace />;
  }
  console.log('✅ Journeys access granted for admin user');
  return children;
}

/**
 * Main App Component
 * 🏠 Sets up routing and authentication context
 */
// Removed MobileNavFallback component to prevent conflicts

function App() {
  // Mobile navigation is handled by MobileLayout.jsx - removed conflicting JavaScript
  const location = useLocation();
  const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);
  
  // Hide feedback button on admin routes
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Define public routes where feedback should not be shown
  const publicRoutes = ['/login', '/register', '/', '/about', '/mission', '/docs', '/forum', '/privacy', '/data-retention', '/verify-invitation', '/request-invitation'];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  
  // Check if user is authenticated (has token in localStorage)
  const isAuthenticated = typeof window !== 'undefined' && localStorage.getItem('token') !== null;
  
  // Only show feedback if user is authenticated, not on admin routes, and not on public routes
  const shouldShowFeedback = isAuthenticated && !isAdminRoute && !isPublicRoute;

  const handleOpenFeedbackModal = () => {
    setFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModalOpen(false);
  };
  
  // Listen for messages to end impersonation sessions
  useEffect(() => {
    const handleMessage = (event) => {
      // Only accept messages from the same origin for security
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'END_IMPERSONATION') {
        // Check if this window is an impersonation session
        const isImpersonating = localStorage.getItem('impersonation') === 'true';
        
        if (isImpersonating) {
          // Clear impersonation data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('impersonation');
          localStorage.removeItem('impersonatedBy');
          localStorage.removeItem('impersonatedAt');
          localStorage.removeItem('demoMode');
          
          // Close this window if it was opened by another window
          if (window.opener && !window.opener.closed) {
            window.close();
          } else {
            // Fallback: redirect to login
            window.location.href = '/login';
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <>
      <ImpersonationBanner />
      <Toaster position="top-right" />
      <WallyChatModal />
      {shouldShowFeedback && <FeedbackButton onOpenModal={handleOpenFeedbackModal} />}
      {shouldShowFeedback && <FeedbackModal open={feedbackModalOpen} onClose={handleCloseFeedbackModal} />}
      <AuthProvider>
        <ThemeProvider>
          {/* Mobile navigation is handled by MobileLayout.jsx - removed conflicting fallback */}
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Welcome />} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={<Welcome />} />
            <Route path="/about" element={<About />} />
            <Route path="/mission" element={<Mission />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/forum" element={<Forum />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/data-retention" element={<DataRetentionPolicy />} />
            
            {/* New Onboarding Flow - DISABLED - Redirect to dashboard */}
            <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
            
            {/* Invitation-related routes */}
            <Route path="/verify-invitation" element={<InvitationVerification />} />
            <Route path="/request-invitation" element={<InvitationRequest />} />
            
            {/* Password reset routes */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            
            {/* Email verification route - protected but special handling */}
            <Route path="/verify-email" element={
              <ProtectedRoute>
                {/* EmailVerification is now handled directly in ProtectedRoute */}
                <></>
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<React.Suspense fallback={<div>Loading...</div>}><AdminLogin /></React.Suspense>} />
            <Route path="/admin/*" element={
              <React.Suspense fallback={<div>Loading...</div>}>
                <AdminProtectedRoute>
                  <AdminPortal />
                </AdminProtectedRoute>
              </React.Suspense>
            } />

            {/* Protected Routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/wallet" element={<Wallet />} />
                      <Route path="/cashflow/*" element={
                        <Routes>
                          <Route index element={<CashFlowDashboard />} />
                          <Route path="income" element={<CashFlowDashboard initialTab="income" />} />
                          <Route path="expenses" element={<CashFlowDashboard initialTab="expenses" />} />
                          <Route path="budget" element={<CashFlowDashboard initialTab="budget" />} />
                          <Route path="debt" element={<CashFlowDashboard initialTab="debt" />} />
                          <Route path="savings" element={<CashFlowDashboard initialTab="savings" />} />
                          <Route path="assets" element={<CashFlowDashboard initialTab="assets" />} />
                        </Routes>
                      } />
                      {/* Journeys - Admin only */}
                      <Route path="/journeys" element={<AdminOnlyRoute><JourneysPage /></AdminOnlyRoute>} />
                      <Route path="/journeys/:id" element={<AdminOnlyRoute><JourneyDetail /></AdminOnlyRoute>} />
                      <Route path="/transactions/*" element={
                        <Routes>
                          <Route index element={<Transactions />} />
                          <Route path="account/:accountId" element={<Transactions />} />
                          <Route path="recurring" element={<RecurringTransactions />} />
                          <Route path="categories" element={<CategoryManagement />} />
                          <Route path="import" element={<ImportTransactions />} />
                        </Routes>
                      } />
                      <Route path="/budgeting/*" element={
                        <Routes>
                          <Route index element={<BudgetList />} />
                          <Route path=":id" element={<BudgetDetail />} />
                          <Route path="new" element={<BudgetForm />} />
                          <Route path=":id/edit" element={<BudgetForm />} />
                          <Route path=":id/actual" element={<BudgetActual />} />
                          <Route path=":id/compare" element={<BudgetCompare />} />
                          <Route path="templates" element={<BudgetTemplatesPage />} />
                          <Route path="templates/new" element={<TemplateForm />} />
                          <Route path="templates/edit/:id" element={<TemplateForm />} />
                          <Route path="templates/from-budget/:budgetId" element={<TemplateForm />} />
                        </Routes>
                      } />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/settings/partner" element={<PartnerSettings />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          {/* Onboarding Modal - uses self-contained useOnboarding hook */}
          {/* <OnboardingModal /> */}
        </ThemeProvider>
      </AuthProvider>
    </>
  );
}

export default App;
